import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Search, ChevronRight } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";

/** Okno dialogu: na mobilu bottom sheet, na desktopu uprostřed. */
export function Okno({ titulek, onClose, children, paticka, testid, sirka = "md:max-w-[520px]" }) {
  return (
    <div className="fixed inset-0 z-[70] flex md:items-center items-end justify-center md:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white w-full ${sirka} md:rounded-card rounded-t-sheet shadow-panel max-h-[88vh] flex flex-col`}>
        <div className="border-b border-line-softer px-5 py-4 flex items-center justify-between gap-3">
          <h2 className="t-sekce font-800 text-ink">{titulek}</h2>
          <button onClick={onClose} aria-label="Zavřít" className="tap text-muted hover:text-ink"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-3">{children}</div>
        {paticka && <div className="border-t border-line-softer px-5 py-3 flex flex-wrap justify-end gap-2">{paticka}</div>}
      </div>
    </div>
  );
}

/** Výběr technologie klienta, kam nabídka ze sestavy půjde. Technologie, kam
    to nejde (objednaná, bez zaměření…), jsou vidět se zdůvodněním. */
export function CilNabidkyDialog({ titulek, popis, kategorieId, onClose, onVyber }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/api/sestavy/cile", { params: { q, kategorie_id: kategorieId || undefined } })
        .then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, kategorieId]);

  const vyber = async (c) => {
    setBusy(c.technologie_id); setChyba("");
    try { await onVyber(c); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); setBusy(null); }
  };

  return (
    <Okno titulek={titulek} onClose={onClose} testid="cil-nabidky">
      {popis && <p className="t-meta text-muted">{popis}</p>}
      <div className="flex items-center gap-2 field"><Search size={15} className="text-muted shrink-0" />
        <input className="bg-transparent outline-none w-full" placeholder="Hledat klienta nebo případ…" autoFocus
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="cil-hledat" /></div>
      {chyba && <div className="t-meta text-danger" data-testid="cil-chyba">{chyba}</div>}
      {items === null ? <div className="py-6 flex justify-center"><Spinner /></div>
        : items.length === 0 ? (
          <p className="t-meta text-muted py-3">Nic jsme nenašli. Technologii klientovi přidáš v jeho obchodním případu.</p>
        ) : (
          <div className="border border-line-softer rounded-card overflow-hidden">
            {items.map((c) => (
              <button key={c.technologie_id} onClick={() => vyber(c)} disabled={!c.lze || !!busy}
                data-testid={`cil-${c.technologie_id}`}
                className="tap w-full text-left flex items-center gap-3 px-4 py-3 border-t border-line-softer first:border-0 hover:bg-inputbg disabled:hover:bg-white disabled:cursor-not-allowed">
                <span className="min-w-0 flex-1">
                  <span className={`block t-telo font-700 truncate ${c.lze ? "text-ink" : "text-muted"}`}>{c.client_name || "—"}</span>
                  <span className="block t-meta text-muted truncate">
                    {c.technologie} · {c.pripad_cislo}{c.pripad_nazev ? ` ${c.pripad_nazev}` : ""}
                  </span>
                  {c.duvod && <span className={`block t-meta truncate ${c.lze ? "text-sec" : "text-danger"}`}>{c.lze ? c.duvod : `Nejde — ${c.duvod}`}</span>}
                </span>
                {busy === c.technologie_id ? <Spinner /> : c.lze && <ChevronRight size={16} className="text-muted shrink-0" />}
              </button>
            ))}
          </div>
        )}
    </Okno>
  );
}

/** Název nové šablony. */
export function NazevSablonyDialog({ titulek = "Uložit jako šablonu", popis, vychozi = "", onClose, onUlozit }) {
  const [nazev, setNazev] = useState(vychozi);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const ulozit = async () => {
    if (!nazev.trim()) return setChyba("Zadej název šablony, např. Dům 140 m².");
    setBusy(true); setChyba("");
    try { await onUlozit(nazev.trim()); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  return (
    <Okno titulek={titulek} onClose={onClose} testid="nazev-sablony"
      paticka={<>
        <button onClick={onClose} className="btn-ghost tap">Zrušit</button>
        <Button onClick={ulozit} disabled={busy} data-testid="nazev-sablony-ulozit">{busy ? <Spinner /> : "Uložit šablonu"}</Button>
      </>}>
      <label className="block">
        <span className="label-up text-muted block mb-1">Název šablony</span>
        <input className="field" value={nazev} onChange={(e) => setNazev(e.target.value)} autoFocus
          placeholder="Dům 140 m²" onKeyDown={(e) => e.key === "Enter" && ulozit()} data-testid="nazev-sablony-input" />
      </label>
      {popis && <p className="t-meta text-muted">{popis}</p>}
      {chyba && <p className="t-meta text-danger">{chyba}</p>}
    </Okno>
  );
}

/** Po kopii nabídky: co se od původní nabídky změnilo v ceníku. */
export function ZmenyCen({ zmeny }) {
  if (!zmeny?.length) return null;
  return (
    <div className="rounded-card bg-[#fdf3e2] px-4 py-3" data-testid="zmeny-cen">
      <div className="t-telo font-700 text-ink">Od původní nabídky se změnily ceny v ceníku ({zmeny.length})</div>
      <div className="t-meta text-sec mb-1">Nová nabídka má aktuální ceny z ceníku.</div>
      {zmeny.slice(0, 6).map((z) => (
        <div key={z.nazev} className="flex justify-between gap-3 t-meta">
          <span className="truncate">{z.nazev}</span>
          <span className="amount shrink-0">{fmtKc(z.puvodni)} → <b>{fmtKc(z.nova)} Kč</b>
            {z.rozdil_pct != null && <span className={z.rozdil_pct > 0 ? "text-danger" : "text-success"}> ({z.rozdil_pct > 0 ? "+" : ""}{z.rozdil_pct} %)</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

/** U technologie klienta: nová nabídka ze šablony (sestava hlavního zařízení
    i s množstvím). Vznikne normální nabídka a otevře se v editoru. */
export function NabidkaZeSablonyDialog({ tech, onClose }) {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    api.get("/api/sablony", { params: { kategorie_id: tech.kategorie_id } })
      .then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [tech.kategorie_id]);

  const pouzij = async (s) => {
    setBusy(s.id); setChyba("");
    try {
      const { data } = await api.post("/api/sestavy/nabidka", {
        technologie_id: tech.id, zarizeni_id: s.zarizeni_id, radky: s.radky, doprava: s.doprava || undefined });
      nav(`/nabidky/${data.nabidka_id}`);
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); setBusy(null); }
  };
  const skupiny = (items || []).reduce((a, s) => { (a[s.zarizeni_nazev] = a[s.zarizeni_nazev] || []).push(s); return a; }, {});

  return (
    <Okno titulek="Nová nabídka ze šablony" onClose={onClose} testid="nabidka-ze-sablony">
      <p className="t-meta text-muted">Šablona doplní hlavní zařízení, příslušenství, materiál, práci i dopravu s obvyklým množstvím. Ceny se vezmou aktuální z ceníku.</p>
      {chyba && <div className="t-meta text-danger">{chyba}</div>}
      {items === null ? <div className="py-6 flex justify-center"><Spinner /></div>
        : !items.length ? (
          <p className="t-meta text-muted py-2">Pro tuto technologii zatím žádná šablona není. Založíš ji v Ceníku na kartě hlavního zařízení,
            nebo z hotové nabídky tlačítkem „Uložit jako šablonu“.</p>
        ) : Object.entries(skupiny).map(([zar, sab]) => (
          <div key={zar}>
            <div className="label-up text-muted mb-1.5">{zar}</div>
            <div className="border border-line-softer rounded-card overflow-hidden">
              {sab.map((s) => (
                <div key={s.id} className="flex items-center gap-2 px-4 py-2.5 border-t border-line-softer first:border-0">
                  <span className="min-w-0 flex-1">
                    <span className="block t-telo font-700 text-ink truncate">{s.nazev}</span>
                    <span className="block t-meta text-muted">{s.radky.length} položek</span>
                  </span>
                  <button onClick={() => nav(`/cenik/zarizeni/${s.zarizeni_id}?sablona=${s.id}&technologie=${tech.id}`)}
                    className="tap t-meta font-700 text-sec hover:text-ink px-2" data-testid={`sablona-upravit-${s.id}`}>Upravit množství</button>
                  <Button onClick={() => pouzij(s)} disabled={!!busy} data-testid={`sablona-pouzit-${s.id}`}>
                    {busy === s.id ? <Spinner /> : "Použít"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
    </Okno>
  );
}
